// The Nutrient apply-rate rules, kept identical to the old Rates & Dosage
// screen: every rate must sit inside the protocol's range, distinct rates must
// be at least one interval apart, and there must be at least as many distinct
// rates as the protocol asks for.

// Subset of the protocol's `product_details.nutrients`, typed here so the page
// does not import the wizard's protocol types.
export interface ProtocolNutrientDetails {
  application_range?: { min?: number; max?: number };
  interval_distance?: number;
  number_of_intervals?: number;
}

export interface ApplyRateInput {
  id: string;
  applyPercent: number | null;
}

export type ApplyRateError =
  | { kind: "range"; plotId: string; min: number; max: number }
  | {
      kind: "spacing";
      plotId: string;
      previous: number;
      current: number;
      distance: number;
    };

export interface ApplyRateValidation {
  errors: ApplyRateError[];
  // False when there are errors, or when fewer distinct rates are used than
  // the protocol requires. Only the former is shown per plot, like the old
  // screen; the latter just keeps the tab from completing.
  isValid: boolean;
}

export const validateApplyRates = (
  rates: ApplyRateInput[],
  details?: ProtocolNutrientDetails | null
): ApplyRateValidation => {
  const min = details?.application_range?.min;
  const max = details?.application_range?.max;
  const distance = details?.interval_distance;
  const required = details?.number_of_intervals;
  const errors: ApplyRateError[] = [];

  if (min !== undefined && max !== undefined) {
    rates.forEach(({ id, applyPercent }) => {
      if (applyPercent !== null && (applyPercent < min || applyPercent > max)) {
        errors.push({ kind: "range", plotId: id, min, max });
      }
    });
  }

  const uniqueRates = Array.from(
    new Set(
      rates
        .map(({ applyPercent }) => applyPercent)
        .filter((rate): rate is number => rate !== null)
    )
  ).sort((a, b) => a - b);

  if (distance !== undefined) {
    for (let i = 1; i < uniqueRates.length; i += 1) {
      const previous = uniqueRates[i - 1];
      const current = uniqueRates[i];
      if (Math.abs(current - previous) < distance) {
        const firstPlot = rates.find(
          ({ applyPercent }) => applyPercent === current
        );
        errors.push({
          kind: "spacing",
          plotId: (firstPlot as ApplyRateInput).id,
          previous,
          current,
          distance,
        });
      }
    }
  }

  const tooFewDistinctRates =
    required !== undefined && uniqueRates.length < required;

  return { errors, isValid: errors.length === 0 && !tooFewDistinctRates };
};
